/**
 * Extract flow elements from metadata when they're not explicitly provided
 */
export const extractFlowElementsFromMetadata = (metadata: any): { records: any[] } | null => {
  if (!metadata) return null;
  
  // Check different possible locations for flow elements
  const flowElements: any[] = [];
  
  // Check for Metadata.flowElements or Metadata.decisions/recordLookups/etc directly
  if (metadata.Metadata) {
    // Flow decisions
    if (Array.isArray(metadata.Metadata.decisions)) {
      metadata.Metadata.decisions.forEach((decision: any) => {
        flowElements.push({
          ElementType: 'Decision',
          ApiName: decision.name,
          Label: decision.label || decision.name,
          TargetReference: '',
          Description: `Decision: ${decision.label || decision.name}`,
          rules: decision.rules || []
        });
      });
    }
    
    // Flow record lookups
    if (Array.isArray(metadata.Metadata.recordLookups)) {
      metadata.Metadata.recordLookups.forEach((lookup: any) => {
        flowElements.push({
          ElementType: 'RecordLookup',
          ApiName: lookup.name,
          Label: lookup.label || lookup.name,
          TargetReference: lookup.object,
          Description: `Lookup ${lookup.object} records`
        });
      });
    }
    
    // Flow record creates
    if (Array.isArray(metadata.Metadata.recordCreates)) {
      metadata.Metadata.recordCreates.forEach((create: any) => {
        flowElements.push({
          ElementType: 'RecordCreate',
          ApiName: create.name,
          Label: create.label || create.name,
          TargetReference: create.object,
          Description: `Create ${create.object} record`,
          InputAssignments: create.inputAssignments || []
        });
      });
    }
    
    // Flow record updates
    if (Array.isArray(metadata.Metadata.recordUpdates)) {
      metadata.Metadata.recordUpdates.forEach((update: any) => {
        flowElements.push({
          ElementType: 'RecordUpdate',
          ApiName: update.name,
          Label: update.label || update.name,
          TargetReference: update.object,
          Description: `Update ${update.object} record`,
          InputAssignments: update.inputAssignments || []
        });
      });
    }
    
    // Flow screens
    if (Array.isArray(metadata.Metadata.screens)) {
      metadata.Metadata.screens.forEach((screen: any) => {
        flowElements.push({
          ElementType: 'Screen',
          ApiName: screen.name,
          Label: screen.label || screen.name,
          Description: `Screen: ${screen.label || screen.name}`,
          Fields: screen.fields || []
        });
      });
    }
  }
  
  // If we found elements, return them in the expected format
  return flowElements.length > 0 ? { records: flowElements } : null;
};

/**
 * Extract variables from metadata when they're not explicitly provided
 */
export const extractFlowVariablesFromMetadata = (metadata: any): { records: any[] } | null => {
  if (!metadata) return null;
  
  // Check different possible locations for flow variables
  const flowVariables: any[] = [];
  
  // Check for Metadata.variables directly
  if (metadata.Metadata) {
    // Flow variables
    if (Array.isArray(metadata.Metadata.variables)) {
      metadata.Metadata.variables.forEach((variable: any) => {
        flowVariables.push({
          Id: variable.name, // Use name as ID since there's no explicit ID
          DeveloperName: variable.name,
          DataType: variable.dataType,
          Value: variable.value,
          Description: variable.description || `${variable.dataType} variable`
        });
      });
    }
    
    // Flow constants
    if (Array.isArray(metadata.Metadata.constants)) {
      metadata.Metadata.constants.forEach((constant: any) => {
        flowVariables.push({
          Id: constant.name,
          DeveloperName: constant.name,
          DataType: constant.dataType,
          Value: constant.value,
          Description: constant.description || 'Constant value'
        });
      });
    }
    
    // Flow formulas
    if (Array.isArray(metadata.Metadata.formulas)) {
      metadata.Metadata.formulas.forEach((formula: any) => {
        flowVariables.push({
          Id: formula.name,
          DeveloperName: formula.name,
          DataType: formula.dataType,
          Value: formula.expression,
          Description: formula.description || 'Formula expression'
        });
      });
    }
  }
  
  // If we found variables, return them in the expected format
  return flowVariables.length > 0 ? { records: flowVariables } : null;
};

/**
 * Helper function to get flow summary with fallbacks
 */
export const getFlowSummary = (flowObj: any, workspaceId?: string): string | null => {
  if (!flowObj) return null;
  
  // If workspaceId is provided, look for workspace-specific summary
  if (workspaceId && flowObj.sf_flow_summaries) {
    // Ensure sf_flow_summaries is an array
    const summaries = Array.isArray(flowObj.sf_flow_summaries) 
      ? flowObj.sf_flow_summaries 
      : [];
    
    const workspaceSummary = summaries.find(
      (s: any) => s.workspace_id === workspaceId
    );
    if (workspaceSummary?.ai_summary) {
      return workspaceSummary.ai_summary;
    }
    // If workspace is specified but no summary exists, return null
    // Don't fall back to other workspace summaries
    return null;
  }
  
  // Only use fallback when no specific workspace is requested
  // This is for backward compatibility with single-workspace setups
  return flowObj.summaries?.[0]?.ai_summary || flowObj.summaries?.ai_summary || flowObj.ai_summary || null;
};

/**
 * Helper function to get flow summary with structured analysis
 */
export const getFlowSummaryWithStructured = (flowObj: any, workspaceId?: string): { narrative: string | null, structured: any | null } => {
  if (!flowObj) return { narrative: null, structured: null };
  
  // If workspaceId is provided, look for workspace-specific summary
  if (workspaceId && flowObj.sf_flow_summaries) {
    // Ensure sf_flow_summaries is an array
    const summaries = Array.isArray(flowObj.sf_flow_summaries) 
      ? flowObj.sf_flow_summaries 
      : [];
    
    const workspaceSummary = summaries.find(
      (s: any) => s.workspace_id === workspaceId
    );
    if (workspaceSummary) {
      return {
        narrative: workspaceSummary.ai_summary || null,
        structured: workspaceSummary.structured_analysis || null
      };
    }
    // If workspace is specified but no summary exists, return null
    return { narrative: null, structured: null };
  }
  
  // Only use fallback when no specific workspace is requested
  // This is for backward compatibility with single-workspace setups
  const legacySummary = flowObj.summaries?.[0] || flowObj.summaries || flowObj;
  return {
    narrative: legacySummary?.ai_summary || null,
    structured: legacySummary?.structured_analysis || null
  };
};

/**
 * Helper function to check if flow has summary
 */
export const hasFlowSummary = (flowObj: any, workspaceId?: string): boolean => {
  return !!getFlowSummary(flowObj, workspaceId);
};

/**
 * Helper function to generate list of objects and fields affected by the flow
 */
export const generateFieldReferencesList = (flow: any): any[] => {
  // Debug flow elements structure
  console.log('generateFieldReferencesList called with flow elements:', {
    hasFlowElements: !!flow.flow_elements,
    recordsCount: flow.flow_elements?.records?.length || 0,
    isRecordsArray: Array.isArray(flow.flow_elements?.records),
    elementTypes: flow.flow_elements ? Object.keys(flow.flow_elements).filter(key => 
      Array.isArray(flow.flow_elements[key]) && flow.flow_elements[key].length > 0
    ) : []
  });
  
  // If no flow elements object exists at all, return empty array
  if (!flow.flow_elements) {
    return [];
  }
  
  // Collect all object and field references
  const fieldReferences: {object: string, field: string, stepName: string}[] = [];
  
  // Process elements from the Salesforce format (groups of different element types)
  // The data structure is different from what we expected - it's not a simple "records" array
  // Instead, it has separate arrays for each element type like recordLookups, recordUpdates, etc.
  
  // Handle record lookups
  if (flow.flow_elements.recordLookups && Array.isArray(flow.flow_elements.recordLookups)) {
    flow.flow_elements.recordLookups.forEach((lookup: any) => {
      // Get reference to the object
      const objectType = lookup.object || 'Unknown';
      
      // Get field references from filters
      if (lookup.filters && Array.isArray(lookup.filters)) {
        lookup.filters.forEach((filter: any) => {
          if (filter.field) {
            fieldReferences.push({
              object: objectType,
              field: filter.field,
              stepName: lookup.label || lookup.name || 'Unknown'
            });
          }
        });
      }
    });
  }
  
  // Handle record updates
  if (flow.flow_elements.recordUpdates && Array.isArray(flow.flow_elements.recordUpdates)) {
    flow.flow_elements.recordUpdates.forEach((update: any) => {
      // Get reference to the object
      const objectType = update.object || 'Unknown';
      
      // Get field references from input assignments
      if (update.inputAssignments && Array.isArray(update.inputAssignments)) {
        update.inputAssignments.forEach((assignment: any) => {
          if (assignment.field) {
            fieldReferences.push({
              object: objectType,
              field: assignment.field,
              stepName: update.label || update.name || 'Unknown'
            });
          }
        });
      }
    });
  }
  
  // Handle record creates
  if (flow.flow_elements.recordCreates && Array.isArray(flow.flow_elements.recordCreates)) {
    flow.flow_elements.recordCreates.forEach((create: any) => {
      // Get reference to the object
      const objectType = create.object || 'Unknown';
      
      // Get field references from input assignments
      if (create.inputAssignments && Array.isArray(create.inputAssignments)) {
        create.inputAssignments.forEach((assignment: any) => {
          if (assignment.field) {
            fieldReferences.push({
              object: objectType,
              field: assignment.field,
              stepName: create.label || create.name || 'Unknown'
            });
          }
        });
      }
    });
  }
  
  return fieldReferences;
};